import { useMemo, useState, type ComponentType } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatInTimeZone } from 'date-fns-tz';
import {
  ArrowUpRight,
  CalendarCheck2,
  CalendarClock,
  ChevronRight,
  CircleDollarSign,
  LockKeyhole,
  MinusCircle,
  ReceiptText,
  TicketCheck,
  UserPlus,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type { AgendaAppointment, AgendaDayResponse } from '@agenda-facil/contracts';
import { Button, Skeleton, StatusPill, Surface, Tabs, cn } from '@agenda-facil/ui';
import { ErrorState } from '../../components/query-feedback';
import {
  ChartEmptyState,
  MetricSparkline,
  OccupancyRing,
  PeakHoursChart,
  RevenueAreaChart,
  RoundedBarChart,
} from '../../features/dashboard/dashboard-charts';
import {
  buildDashboardPeriod,
  buildPeakHours,
  calculateOccupancy,
  formatMinutes,
  isActiveAppointment,
  type ChartPoint,
  type DashboardMetrics,
  type DashboardPeriod,
  type DashboardPeriodConfig,
  type DashboardRange,
} from '../../features/dashboard/dashboard-insights';
import type { BarberRecord, OwnerShop } from '../../features/onboarding/types';
import {
  appointmentStatusLabel,
  appointmentStatusTone,
} from '../../features/agenda/appointment-status';
import { api } from '../../lib/api';

const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const integer = new Intl.NumberFormat('pt-BR');

interface DashboardViewData {
  config: DashboardPeriodConfig;
  summary: DashboardMetrics;
  seriesMetrics: DashboardMetrics[];
  agendaDays: AgendaDayResponse[];
}

function metricsPath(range: DashboardRange): string {
  return `/dashboard?${new URLSearchParams({ from: range.from, to: range.to }).toString()}`;
}

async function loadDashboard(
  period: DashboardPeriod,
  timezone: string,
): Promise<DashboardViewData> {
  const config = buildDashboardPeriod(period, timezone);
  const [summary, seriesMetrics, agendaDays] = await Promise.all([
    api.get<DashboardMetrics>(metricsPath(config.summary)),
    Promise.all(config.series.map((range) => api.get<DashboardMetrics>(metricsPath(range)))),
    Promise.all(
      config.dates.map((date) => api.get<AgendaDayResponse>(`/appointments?date=${date}`)),
    ),
  ]);
  return { config, summary, seriesMetrics, agendaDays };
}

export function DashboardPage() {
  const [period, setPeriod] = useState<DashboardPeriod>('today');
  const shop = useQuery({
    queryKey: ['owner-shop'],
    queryFn: () => api.get<OwnerShop>('/shops/me'),
  });
  const barbers = useQuery({
    queryKey: ['barbers', shop.data?.id],
    queryFn: () => api.get<BarberRecord[]>(`/shops/${shop.data?.id}/barbers`),
    enabled: Boolean(shop.data?.id),
  });
  const dashboard = useQuery({
    queryKey: ['dashboard-insights', period, shop.data?.timezone],
    queryFn: () => loadDashboard(period, shop.data?.timezone ?? 'America/Sao_Paulo'),
    enabled: Boolean(shop.data?.timezone && barbers.data),
    staleTime: 30_000,
  });

  if (shop.isLoading || barbers.isLoading || dashboard.isLoading) return <DashboardSkeleton />;
  if (shop.isError || barbers.isError || dashboard.isError) {
    return (
      <ErrorState
        message="Não foi possível carregar os indicadores operacionais."
        retry={() => {
          void shop.refetch();
          void barbers.refetch();
          void dashboard.refetch();
        }}
      />
    );
  }
  if (!shop.data || !barbers.data || !dashboard.data) return null;

  return (
    <DashboardContent
      key={period}
      period={period}
      setPeriod={setPeriod}
      shop={shop.data}
      barbers={barbers.data}
      data={dashboard.data}
    />
  );
}

function DashboardContent({
  period,
  setPeriod,
  shop,
  barbers,
  data,
}: {
  period: DashboardPeriod;
  setPeriod: (period: DashboardPeriod) => void;
  shop: OwnerShop;
  barbers: BarberRecord[];
  data: DashboardViewData;
}) {
  const allAppointments = useMemo(
    () => data.agendaDays.flatMap((day) => day.appointments),
    [data.agendaDays],
  );
  const activeAppointments = allAppointments.filter(isActiveAppointment);
  const occupancy = calculateOccupancy(data.config.dates, allAppointments, barbers);
  const peakHours = buildPeakHours(allAppointments, shop.timezone);

  const revenuePoints = data.config.series.map((range, index) => ({
    label: range.label,
    value: data.seriesMetrics[index]?.revenueCents ?? 0,
  }));
  const appointmentPoints = data.config.series.map((range, index) => ({
    label: range.label,
    value: data.seriesMetrics[index]?.appointments ?? 0,
  }));
  const completedPoints = data.config.series.map((range, index) => ({
    label: range.label,
    value: data.seriesMetrics[index]?.completed ?? 0,
  }));
  const customerPoints = data.config.series.map((range, index) => ({
    label: range.label,
    value: data.seriesMetrics[index]?.newCustomers ?? 0,
  }));
  const busiestPoint = maxPoint(appointmentPoints);
  const peakPoint = peakHours[0];
  const upcoming = activeAppointments
    .filter((appointment) => new Date(appointment.startsAt).getTime() >= Date.now())
    .sort((left, right) => left.startsAt.localeCompare(right.startsAt))
    .slice(0, 4);

  const metricItems: MetricItem[] = [
    {
      label: 'Faturamento registrado',
      value: currency.format(data.summary.revenueCents / 100),
      context: period === 'today' ? 'Vendas concluídas hoje' : 'Vendas concluídas na semana',
      icon: CircleDollarSign,
      points: revenuePoints,
      tone: 'accent',
    },
    {
      label: 'Agendamentos',
      value: integer.format(data.summary.appointments),
      context: period === 'today' ? 'Reservas do dia' : 'Reservas da semana',
      icon: CalendarCheck2,
      points: appointmentPoints,
      tone: 'brand',
    },
    {
      label: 'Concluídos',
      value: integer.format(data.summary.completed),
      context: 'Atendimentos finalizados',
      icon: TicketCheck,
      points: completedPoints,
      tone: 'success',
    },
    {
      label: 'Novos clientes',
      value: integer.format(data.summary.newCustomers),
      context: 'Cadastros no período',
      icon: UserPlus,
      points: customerPoints,
      tone: 'neutral',
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <span className="eyebrow">Visão operacional</span>
          <h1 className="display mt-2 text-3xl font-extrabold text-[var(--foreground)] sm:text-4xl">
            Painel de {shop.name}
          </h1>
          <p className="mt-2 max-w-xl text-sm leading-6 text-[var(--muted-foreground)]">
            Faturamento, agenda e ritmo de atendimento no mesmo lugar.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Tabs
            ariaLabel="Período dos indicadores"
            activeId={period}
            onChange={(id) => setPeriod(id as DashboardPeriod)}
            items={[
              { id: 'today', label: 'Hoje' },
              { id: 'week', label: 'Esta semana' },
            ]}
          />
          {shop.publishedAt ? (
            <Button size="sm" asChild className="min-h-11">
              <Link to={`/barbearia/${shop.slug}/agendar`}>
                Página pública <ArrowUpRight size={15} aria-hidden="true" />
              </Link>
            </Button>
          ) : (
            <Button
              size="sm"
              className="min-h-11"
              disabled
              title="Publique a barbearia para abrir a página pública."
            >
              Página não publicada
            </Button>
          )}
        </div>
      </header>

      <MetricStrip items={metricItems} />

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.45fr)_minmax(300px,.75fr)]">
        <Surface className="motion-panel-in min-w-0 p-5 sm:p-6">
          <PanelHeading
            eyebrow={period === 'today' ? 'Distribuição do dia' : 'Distribuição da semana'}
            title="Faturamento"
            detail={currency.format(data.summary.revenueCents / 100)}
          />
          <div className="mt-5">
            <RevenueAreaChart
              points={revenuePoints}
              ariaLabel={`Faturamento ${period === 'today' ? 'por faixa de horário' : 'por dia da semana'}`}
              valueFormatter={(value) => currency.format(value / 100)}
            />
          </div>
          <div className="mt-4 grid gap-3 border-t border-[var(--border)] pt-4 text-xs sm:grid-cols-2">
            <SummaryDatum
              label="Ticket médio"
              value={currency.format(data.summary.averageTicketCents / 100)}
            />
            <SummaryDatum
              label="Maior faturamento"
              value={
                maxPoint(revenuePoints)?.value
                  ? `${maxPoint(revenuePoints)?.label}, ${currency.format((maxPoint(revenuePoints)?.value ?? 0) / 100)}`
                  : 'Sem vendas no período'
              }
            />
          </div>
        </Surface>

        <Surface className="motion-panel-in p-5 sm:p-6">
          <PanelHeading eyebrow="Tempo reservado" title="Ocupação" />
          {occupancy.percentage === undefined ? (
            <div className="mt-5">
              <ChartEmptyState message="Cadastre as escalas dos profissionais para calcular a ocupação real." />
            </div>
          ) : (
            <div className="mt-6">
              <div className="flex flex-col items-center gap-5 sm:flex-row xl:flex-col 2xl:flex-row">
                <OccupancyRing percentage={occupancy.percentage} />
                <div className="min-w-0 flex-1 space-y-4">
                  <SummaryDatum
                    label="Tempo agendado"
                    value={formatMinutes(occupancy.occupiedMinutes)}
                  />
                  <SummaryDatum
                    label="Tempo disponível"
                    value={formatMinutes(occupancy.capacityMinutes)}
                  />
                  <SummaryDatum
                    label="Reservas ativas"
                    value={integer.format(activeAppointments.length)}
                  />
                </div>
              </div>
              <p className="mt-5 rounded-2xl bg-[var(--input)] p-3 text-xs leading-5 text-[var(--muted-foreground)]">
                Calculada pela duração real das reservas sobre as escalas ativas da equipe.
              </p>
            </div>
          )}
        </Surface>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(300px,.9fr)]">
        <Surface className="motion-panel-in p-5 sm:p-6">
          <PanelHeading
            eyebrow={period === 'today' ? 'Por faixa de horário' : 'Por dia da semana'}
            title={period === 'today' ? 'Volume de atendimentos' : 'Dias mais movimentados'}
            detail={
              busiestPoint?.value ? `${busiestPoint.label}: ${busiestPoint.value}` : undefined
            }
          />
          <RoundedBarChart
            points={appointmentPoints}
            ariaLabel={
              period === 'today'
                ? 'Quantidade de atendimentos por faixa de horário'
                : 'Quantidade de atendimentos por dia da semana'
            }
          />
        </Surface>

        <Surface className="motion-panel-in p-5 sm:p-6">
          <PanelHeading
            eyebrow="Inícios agendados"
            title="Horários de maior movimento"
            detail={peakPoint ? `${peakPoint.label}: ${peakPoint.value}` : undefined}
          />
          <div className="mt-7">
            <PeakHoursChart points={peakHours} />
          </div>
        </Surface>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(320px,.8fr)]">
        <Surface className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <PanelHeading eyebrow="Agenda" title="Próximos atendimentos" />
            <Button variant="ghost" size="sm" asChild className="min-h-11">
              <Link to="/app/agenda">
                Ver agenda <ChevronRight size={14} aria-hidden="true" />
              </Link>
            </Button>
          </div>
          {upcoming.length > 0 ? (
            <div
              className="mt-5 divide-y divide-[var(--border)]"
              aria-label="Próximos atendimentos"
            >
              {upcoming.map((appointment) => (
                <UpcomingAppointment
                  key={appointment.id}
                  appointment={appointment}
                  timezone={shop.timezone}
                />
              ))}
            </div>
          ) : (
            <InlineEmptyState
              icon={CalendarClock}
              title="Nenhum atendimento próximo"
              message="Novas reservas aparecerão aqui assim que entrarem na agenda."
            />
          )}
        </Surface>

        <CashRegisterPanel />
      </div>

      <Surface className="grid gap-4 p-4 sm:grid-cols-3 sm:p-5">
        <OperationalDatum
          label="Ticket médio"
          value={currency.format(data.summary.averageTicketCents / 100)}
        />
        <OperationalDatum label="Cancelamentos" value={integer.format(data.summary.canceled)} />
        <OperationalDatum label="Faltas" value={integer.format(data.summary.noShow)} />
      </Surface>
    </div>
  );
}

interface MetricItem {
  label: string;
  value: string;
  context: string;
  icon: ComponentType<{ size?: number; className?: string; 'aria-hidden'?: boolean }>;
  points: ChartPoint[];
  tone: 'accent' | 'brand' | 'success' | 'neutral';
}

function MetricStrip({ items }: { items: MetricItem[] }) {
  return (
    <Surface className="grid grid-cols-2 overflow-hidden p-0 xl:grid-cols-4">
      {items.map((item, index) => {
        const Icon = item.icon;
        return (
          <section
            key={item.label}
            className={cn(
              'motion-panel-in min-w-0 p-4 sm:p-5',
              index % 2 === 1 && 'border-l border-[var(--border)]',
              index >= 2 && 'border-t border-[var(--border)] xl:border-t-0',
              index > 0 && 'xl:border-l',
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-bold text-[var(--muted-foreground)]">{item.label}</p>
                <p className="mt-3 text-xl font-black tracking-tight text-[var(--foreground)] sm:text-2xl">
                  {item.value}
                </p>
              </div>
              <span
                className={cn(
                  'grid size-10 shrink-0 place-items-center rounded-2xl',
                  item.tone === 'accent' && 'bg-[var(--accent-soft)] text-[var(--accent)]',
                  item.tone === 'brand' &&
                    'bg-[color-mix(in_srgb,var(--brand)_12%,var(--surface))] text-[var(--brand)]',
                  item.tone === 'success' && 'bg-[var(--success-soft)] text-[var(--success)]',
                  item.tone === 'neutral' && 'bg-[var(--surface-raised)] text-[var(--foreground)]',
                )}
              >
                <Icon size={18} aria-hidden={true} />
              </span>
            </div>
            <div className="mt-4 flex items-end justify-between gap-3">
              <p className="text-[10px] font-semibold leading-4 text-[var(--muted-foreground)]">
                {item.context}
              </p>
              <MetricSparkline
                points={item.points}
                className={
                  item.tone === 'brand'
                    ? 'text-[var(--brand)]'
                    : item.tone === 'success'
                      ? 'text-[var(--success)]'
                      : undefined
                }
              />
            </div>
          </section>
        );
      })}
    </Surface>
  );
}

function PanelHeading({
  eyebrow,
  title,
  detail,
}: {
  eyebrow: string;
  title: string;
  detail?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[var(--muted-foreground)]">
          {eyebrow}
        </p>
        <h2 className="mt-1 text-base font-black text-[var(--foreground)]">{title}</h2>
      </div>
      {detail && (
        <span className="rounded-xl bg-[var(--accent-soft)] px-2.5 py-1 text-[10px] font-black text-[var(--accent)]">
          {detail}
        </span>
      )}
    </div>
  );
}

function SummaryDatum({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--muted-foreground)]">
        {label}
      </p>
      <p className="mt-1 text-sm font-black text-[var(--foreground)]">{value}</p>
    </div>
  );
}

function OperationalDatum({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl bg-[var(--input)] px-4 py-3 sm:block sm:bg-transparent sm:px-3">
      <p className="text-xs font-bold text-[var(--muted-foreground)]">{label}</p>
      <p className="text-lg font-black tabular-nums text-[var(--foreground)] sm:mt-1">{value}</p>
    </div>
  );
}

function UpcomingAppointment({
  appointment,
  timezone,
}: {
  appointment: AgendaAppointment;
  timezone: string;
}) {
  return (
    <div className="flex items-center gap-3 py-4 first:pt-0 last:pb-0">
      <time
        dateTime={appointment.startsAt}
        className="grid size-12 shrink-0 place-items-center rounded-2xl bg-[var(--accent-soft)] text-xs font-black tabular-nums text-[var(--accent)]"
      >
        {formatInTimeZone(appointment.startsAt, timezone, 'HH:mm')}
      </time>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-extrabold text-[var(--foreground)]">
          {appointment.customerName}
        </p>
        <p className="mt-0.5 truncate text-xs text-[var(--muted-foreground)]">
          {appointment.serviceName} · {appointment.barberName}
        </p>
      </div>
      <StatusPill tone={appointmentStatusTone[appointment.status]}>
        {appointmentStatusLabel[appointment.status]}
      </StatusPill>
    </div>
  );
}

function CashRegisterPanel() {
  return (
    <Surface className="p-5 sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[var(--muted-foreground)]">
            Operação financeira
          </p>
          <h2 className="mt-1 text-base font-black text-[var(--foreground)]">Caixa do dia</h2>
        </div>
        <StatusPill tone="neutral">Não conectado</StatusPill>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 rounded-2xl bg-[var(--input)] p-4">
        <SummaryDatum label="Valor atual" value="—" />
        <SummaryDatum label="Aberto às" value="—" />
      </div>

      <div
        className="mt-5 grid grid-cols-3 gap-2"
        aria-label="Ações rápidas do caixa indisponíveis"
      >
        <CashAction icon={ReceiptText} label="Nova venda" />
        <CashAction icon={MinusCircle} label="Sangria" />
        <CashAction icon={LockKeyhole} label="Fechar" />
      </div>

      <p className="mt-5 text-xs leading-5 text-[var(--muted-foreground)]">
        A API ainda não disponibiliza leitura ou ações do caixa. Nenhum saldo é estimado.
      </p>
      <Button variant="ghost" size="sm" asChild className="mt-3 min-h-11 w-full">
        <Link to="/app/caixa">
          Ver disponibilidade do módulo <ChevronRight size={14} aria-hidden="true" />
        </Link>
      </Button>
    </Surface>
  );
}

function CashAction({
  icon: Icon,
  label,
}: {
  icon: ComponentType<{ size?: number; 'aria-hidden'?: boolean }>;
  label: string;
}) {
  return (
    <button
      type="button"
      disabled
      title={`${label} estará disponível quando o módulo de caixa for ativado.`}
      className="flex min-h-16 flex-col items-center justify-center gap-1.5 rounded-xl border border-[var(--border)] bg-[var(--surface)] px-2 text-[10px] font-extrabold text-[var(--muted-foreground)] opacity-55"
    >
      <Icon size={17} aria-hidden={true} />
      {label}
    </button>
  );
}

function InlineEmptyState({
  icon: Icon,
  title,
  message,
}: {
  icon: ComponentType<{ size?: number; 'aria-hidden'?: boolean }>;
  title: string;
  message: string;
}) {
  return (
    <div className="mt-5 flex min-h-40 items-center gap-4 rounded-2xl border border-dashed border-[var(--border)] bg-[var(--input)] p-5">
      <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-[var(--surface-raised)] text-[var(--muted-foreground)]">
        <Icon size={19} aria-hidden={true} />
      </span>
      <div>
        <h3 className="text-sm font-extrabold text-[var(--foreground)]">{title}</h3>
        <p className="mt-1 text-xs leading-5 text-[var(--muted-foreground)]">{message}</p>
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6" role="status" aria-label="Carregando indicadores do painel">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-3">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-10 w-72 max-w-full" />
          <Skeleton className="h-4 w-96 max-w-full" />
        </div>
        <Skeleton variant="button" className="w-56" />
      </div>
      <Surface className="grid overflow-hidden p-0 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <div
            key={index}
            className="space-y-4 border-[var(--border)] p-5 sm:border-l first:sm:border-l-0"
          >
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-8 w-24" />
            <Skeleton className="h-7 w-full" />
          </div>
        ))}
      </Surface>
      <div className="grid gap-6 xl:grid-cols-[1.45fr_.75fr]">
        <Skeleton variant="card" className="min-h-96" />
        <Skeleton variant="card" className="min-h-96" />
      </div>
    </div>
  );
}

function maxPoint(points: ChartPoint[]): ChartPoint | undefined {
  if (points.length === 0) return undefined;
  return points.reduce((highest, point) => (point.value > highest.value ? point : highest));
}
